// Client session — holds the signed session token and performs the SSO-blob → token exchange.
// Shared by every SPA client (api.ts, agencyApi.ts, customerApi.ts) so one identity drives all calls.
//
// Flow: obtain HighLevel's encrypted SSO blob (exposeSessionDetails / postMessage), POST it to
// /sso/decrypt, receive a signed token, and send it as `Authorization: Bearer …` on every request.
// The worker verifies it and derives the tenant server-side — the client never asserts an agencyId.
import type { Me } from './types';

const KEY = 'ss_session_token';
let token: string | null = (() => { try { return sessionStorage.getItem(KEY); } catch { return null; } })();

export function getToken(): string | null { return token; }
export function setToken(t: string | null): void {
  token = t;
  try { if (t) sessionStorage.setItem(KEY, t); else sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}
export function authHeaders(): Record<string, string> {
  return token ? { authorization: `Bearer ${token}` } : {};
}

// Ask the GHL host for the encrypted SSO blob: the Custom-JS runtime exposes
// exposeSessionDetails(APP_ID); a Custom Page answers a postMessage handshake.
function requestEncryptedSession(timeoutMs = 4000): Promise<string | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const APP_ID = (window as unknown as { __SS_APP_ID?: string }).__SS_APP_ID || '';
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: string | null) => { if (!done) { done = true; window.removeEventListener('message', onMsg); resolve(v); } };
    try {
      const f = (window as unknown as { exposeSessionDetails?: (id: string) => unknown }).exposeSessionDetails;
      if (typeof f === 'function') { Promise.resolve(f(APP_ID)).then((v) => finish(typeof v === 'string' ? v : null)).catch(() => finish(null)); return; }
    } catch { /* ignore */ }
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { message?: string; payload?: unknown } | null;
      if (d && d.message === 'REQUEST_USER_DATA_RESPONSE') finish(typeof d.payload === 'string' ? d.payload : null);
    };
    window.addEventListener('message', onMsg);
    try { window.parent?.postMessage({ message: 'REQUEST_USER_DATA' }, '*'); } catch { /* ignore */ }
    setTimeout(() => finish(null), timeoutMs);
  });
}

// Exchange the SSO blob for a fresh signed token; returns the resolved view context (/me) too.
// Called on load and again whenever a request 401s (token expired → silent re-exchange).
let inflight: Promise<Me | null> | null = null;
export function exchangeSession(): Promise<Me | null> {
  if (inflight) return inflight;
  inflight = (async () => {
    const encrypted = await requestEncryptedSession();
    if (!encrypted) return null;
    try {
      const res = await fetch('/api/sso/decrypt', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ encryptedData: encrypted }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { token?: string; me?: Me };
      if (data.token) setToken(data.token);
      return data.me ?? null;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
